import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSlate } from 'slate-react';
import { Transforms } from 'slate';
import clsx from 'clsx';
import { getNodeDeserializer,  } from '@udecode/slate-plugins'
import SignatureCanvas from 'react-signature-canvas'

import { removeNodesById, getNodesById } from '../editor-utils';
import { noop } from 'utils';
import BaseElement from './BaseElement'

export function withSignatures(editor) {
  const { isVoid } = editor;

  // editor.isVoid = (element) => {
  //   return element.type === "signature" ? true : isVoid(element);
  // };

  return editor;
}

export function renderSignatureElement(params) {

  return (props) => {
    if (props.element.type === 'signature') {
      return (
        <SignaturesElement {...props} {...params} />
      );
    }

    return undefined;
  }
}

export function deserializeSignature(options) {
  const type = 'signature';
  return {
    element: getNodeDeserializer({
      type,
      rules: [{ className: 'signature-content' }],
      node: (el) => {
        const signatures = Array.from(el.children);

        const participants = signatures.map(signature => {
          return signature.dataset.participant;
        })

        return ({ type, participants })
      },
    })
  };
}

export const SignaturePlugin = ({ contract = {}, participants = new Map(), ...params } = {}) => ({
  renderElement: renderSignatureElement({ contract, participants, ...params }),
  renderElementDeps: [contract, participants, params.showRemoveSignature, params.showSignatureBorder, params.showSignatureConfirmation, params.signatureConfirmations],
  deserialize: deserializeSignature(),
  voidTypes: ['signature']
});

function getIndividualSignatureProperties({ jurisdiction, participant }) {
  switch (jurisdiction) {
    case 'pr':
      return {
        upper: (
          <React.Fragment>
            <SignatureProperty name="label" value={participant.label} />
          </React.Fragment>
        ),
        lower: (
          <React.Fragment>
            <SignatureProperty className="br-jurisdiction" name="name" value={`Nombre: ${participant.name ?? ''}`} />
            <SignatureProperty className="br-jurisdiction" name="lastName" value={`Apellido: ${participant.last_name ?? ''}`} />
            <SignatureProperty className="br-jurisdiction" name="dni" value={`DNI/C.E./otro: ${participant.cpf || ''}`} />
            {/* <span className="br-jurisdiction">Fecha de Firma: <span contenteditable="false" tabindex="1" value="" placeholder="Fecha de firma" data-participant="${participant._id}" data-id="${participant._id}" data-apiname="signedAt" data-type="constant" data-index="participant${index + 1}" className="variable">Fecha de firma</span></span> */}
          </React.Fragment>
        )
      }
    case 'br':
      return {
        upper: (
          <React.Fragment>
            <SignatureProperty name="label" value={participant.label} />
          </React.Fragment>
        ),
        lower: (
          <React.Fragment>
            <SignatureProperty className="br-jurisdiction" name="name" value={`Nome: ${participant.name ?? ''} ${participant.last_name ?? ''}`} />
            <SignatureProperty className="br-jurisdiction" name="cargo" value={`Cargo: ${participant.cargo ?? ''}`} />
            <SignatureProperty className="br-jurisdiction" name="rg" value={`RG: ${participant.rg ?? ''}`} />
            <SignatureProperty className="br-jurisdiction" name="cpf" value={`CPF: ${participant.cpf ?? ''}`} />
          </React.Fragment>
        )
      }
    case 'mx':
    case 'us':
    default:
      return {
        lower: (
          <React.Fragment>
            <SignatureProperty name="name" value={`${participant.name ?? ''} ${participant.last_name ?? ''}`} />
            <SignatureProperty name="label" value={participant.label} />
          </React.Fragment>
        )
      }
  }
}


function getLegalSignatureProperties({ jurisdiction, participant }) {
  switch (jurisdiction) {
    case 'pr':
      return {
        upper: (
          <React.Fragment>
            <SignatureProperty name="label" value={participant.label} />
          </React.Fragment>
        ),
        lower: (
          <React.Fragment>
            <SignatureProperty className="br-jurisdiction" name="name" value={`Razón Social: ${participant.name}`} />
            <SignatureProperty className="br-jurisdiction" name="representative" value={`Representante: ${participant.representative}`} />
            <SignatureProperty className="br-jurisdiction" name="cargo" value={`Cargo: ${participant.cargo}`} />
            <SignatureProperty className="br-jurisdiction" name="cpf" value={`DNI/C.E./otro: ${participant.cpf}`} />
            {/* <span className="br-jurisdiction">Fecha de Firma: <span contenteditable="false" tabindex="1" value="" placeholder="Fecha de firma" data-participant="${participant._id}" data-id="${participant._id}" data-apiname="signedAt" data-type="constant" data-index="participant${index + 1}" className="variable">Fecha de firma</span></span> */}
          </React.Fragment>
        )
      }
    case 'br':
      return {
        upper: (
          <React.Fragment>
            <SignatureProperty name="label" value={participant.label} />
          </React.Fragment>
        ),
        lower: (
          <React.Fragment>
            <SignatureProperty className="br-jurisdiction" name="name" value={`Nome da empresa: ${participant.name}`} />
            <SignatureProperty className="br-jurisdiction" name="representative" value={`Nome: ${participant.representative}`} />
            <SignatureProperty className="br-jurisdiction" name="cargo" value={`Cargo: ${participant.cargo}`} />
            <SignatureProperty className="br-jurisdiction" name="cpf" value={`CPF: ${participant.cpf}`} />
            <SignatureProperty className="br-jurisdiction" name="rg" value={`RG: ${participant.rg}`} />
          </React.Fragment>
        )
      }
    case 'mx':
      return {
        upper: (
          <React.Fragment>
            <SignatureProperty name="label" value={participant.label} />
            <SignatureProperty name="name" value={participant.name} />
          </React.Fragment>
        ),
        lower: (
          <React.Fragment>
            <SignatureProperty name="name" value={participant.representative} />
            <SignatureProperty name="label" value="Representante Legal" />
          </React.Fragment>
        )
      }
    case 'us':
    default:
      return {
        upper: (
          <React.Fragment>
            <SignatureProperty name="label" value={participant.label} />
            <SignatureProperty name="name" value={participant.name} />
          </React.Fragment>
        ),
        lower: (
          <React.Fragment>
            <SignatureProperty name="name" value={participant.representative} />
          </React.Fragment>
        )
      }
  }
}


export function LegalSignatureElement({
  jurisdiction,
  participant,
  ...props
}) {

  const properties = useMemo(() => getLegalSignatureProperties({
    participant,
    jurisdiction,
  }), [participant, jurisdiction])

  return (
    <SignatureElement
      {...props}
      participant={participant}
      upper={properties.upper}
      lower={properties.lower}
    />
  );
}

export function IndividualSignatureElement({
  jurisdiction,
  participant,
  ...props
}) {
  const properties = useMemo(() => getIndividualSignatureProperties({
    participant,
    jurisdiction,
  }), [participant, jurisdiction])

  return (
    <SignatureElement
      {...props}
      participant={participant}
      upper={properties.upper}
      lower={properties.lower}
    />
  );
}

export default function SignaturesElement({
  participants,
  contract: document,
  signatureConfirmations = {},
  attributes,
  children,
  element,
  ...props
}) {
  const editor = useSlate();

  const participantsSignatures = useMemo(
    () => {
      return element.participants.map((participantId) => {
        const participant = participants.get(participantId) ?? {};

        return (
          {
            ...participant,
            Component: participant.obligation === 'legal' ? LegalSignatureElement : IndividualSignatureElement,
          }
        )
      })
    }, [element.participants, participants]
  )


  const handleRemoveParticipant = (participant) => {
    const [node, location] = getNodesById(editor, element._id)[0];
    const participantIds = node.participants.filter(p => p !== participant._id);
    
    if (participantIds.length === 0) return;

    Transforms.setNodes(editor, { participants: participantIds }, {
      at: location
    })
  }

  const handleConfirmSignature = (participant) => {
    props.onConfirmSignature(participant, element);
  }

  if (!participantsSignatures.length) {
    return null;
  }

  return (
    <BaseElement element={element} contract={document} {...props}>
      {() =>
        <div className="signatures-content" {...attributes}>
          {participantsSignatures.map((participant) => (
            <participant.Component
              key={participant._id}
              {...props}
              jurisdiction={document.jurisdiction}
              participant={participant}
              participantId={participant._id}
              isSignatureConfirmed={!!signatureConfirmations[element._id]}
              onRemoveParticipant={handleRemoveParticipant}
              onConfirmSignature={handleConfirmSignature}
            />
          ))}
          {children}
        </div>
      }
    </BaseElement>

  );
}

// SignatureElement.propTypes = {
//   participantId: PropTypes.string.isRequired,
//   label: PropTypes.string.isRequired,
//   name: PropTypes.string.isRequired,
//   obligation: PropTypes.string.isRequired,
//   jurisdiction: PropTypes.string.isRequired,
//   representative: PropTypes.string
// };

function SignatureElement({ participantId, participant, upper, lower, showRemoveSignature, showSignatureBorder, showSignatureConfirmation, currentParticipantId, isSignatureConfirmed, onConfirmSignature,  onRemoveParticipant, }) {
  const handleRemoveParticipant = () => {
    onRemoveParticipant(participant)
  }
  const handleConfirmSignature = () => {
    onConfirmSignature(participant)
  }

  return (
    <SignatureContainer 
      participantId={participantId} 
      showSignatureBorder={showSignatureBorder}
      currentParticipantId={currentParticipantId}
      showSignatureConfirmation={currentParticipantId === participantId && showSignatureConfirmation}
      showRemoveSignature={showRemoveSignature}
      isSignatureConfirmed={isSignatureConfirmed}
      onConfirmSignature={handleConfirmSignature}
      onRemoveParticipant={handleRemoveParticipant} 
    >
      { upper && <SignatureUpperSection>
        {upper}
      </SignatureUpperSection>}
      <SignatureMiddleSection>
        <SignatureArea participantId={participantId} participant={participant} />
      </SignatureMiddleSection>
      { lower && <SignatureLowerSection>
        {lower}
      </SignatureLowerSection>}
    </SignatureContainer>
  )
}




function SignatureContainer({ participantId, showSignatureBorder = true, showRemoveSignature, showSignatureConfirmation,isSignatureConfirmed, onConfirmSignature , onRemoveParticipant, children }) {

  return (
    <div
      contentEditable={false}
      className={clsx('signature-element', !showSignatureBorder && 'border-0')}
      data-participant={participantId}
    >
      {showRemoveSignature && 
        <span className="remove-button" data-action="remove" onClick={onRemoveParticipant}>
          <i className="fas fa-times fa-fw" />
        </span> 
      }
      {
        showSignatureConfirmation &&
        <div className="check-sign-class" onClick={onConfirmSignature}>
            <input
                className="signature-checkbox"
                type="checkbox"
                checked={isSignatureConfirmed}
                readOnly={true}
            />
          <label htmlFor="_checkbox0" > {/*  className={clsx("checkbox-highlighted")} signState.focused &&  */}
                <div id="tick_mark"></div>
            </label>
        </div>
      }
     
      {children}
    </div>
  )
}

function SignatureArea({ participantId, participant }) {
  const signatureRef = useRef();

  useEffect(() => {
    if (participant.signatureCoords) {
      signatureRef.current.fromData(JSON.parse(participant?.signatureCoords?.coords));
    }
  }, [participant])

  useEffect(() => {
    signatureRef.current.off();
  }, [])

  return (
    <div className="signature-element-area">
      <img className="image-object hide" src="" alt="signature" />
      {/* <canvas id={participantId} /> */}
      <SignatureCanvas canvasProps={{ id: participantId, className: 'image-object' }} ref={signatureRef} maxWidth={2} />
    </div>
  )
}

function SignatureProperty({ className, name, value }) {
  const properties = {
    [`participant-${name}`]: value
  }

  return (
    <span className={clsx('property', className)} {...properties}>{value}</span>
  )
}

function SignatureUpperSection({ participantId, children }) {
  return (
    <div className="upper-section">
      {children}
    </div>
  )
}

function SignatureMiddleSection({ participantId, children }) {
  return (
    <div className="middle-section">
      {children}
    </div>
  )
}
function SignatureLowerSection({ participantId, children }) {
  return (
    <div className="lower-section">
      {children}
    </div>
  )
}